import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, ShoppingCart, Utensils } from 'lucide-react';

export const RestaurantPortfolio: React.FC = () => {
  const technologies = [
    'WordPress', 'WooCommerce', 'PHP', 'MySQL', 'Stripe API', 'AJAX'
  ];

  const features = [
    'Online Ordering System',
    'Menu Management',
    'Delivery Integration',
    'Table Reservations',
    'Loyalty Program',
    'Mobile App',
    'Payment Processing',
    'Order Tracking'
  ];

  const results = [
    { metric: 'Online Orders', value: '250%', description: 'Increase in online order volume' },
    { metric: 'Revenue Growth', value: '40%', description: 'Overall revenue increase' },
    { metric: 'Customer Retention', value: '65%', description: 'Improvement through loyalty program' },
    { metric: 'Order Accuracy', value: '98%', description: 'Accuracy rate with new system' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Restaurant
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Bella Vista Italian Bistro
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A comprehensive restaurant platform with online ordering, delivery integration, 
                and loyalty program that increased online orders by 250% and overall revenue by 40%.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Restaurant Interior"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  Bella Vista Italian Bistro needed to adapt to the growing demand for online ordering 
                  and delivery services while maintaining their reputation for quality and customer service. 
                  Their existing website was outdated and didn't support e-commerce functionality.
                </p>
                <p className="mb-6">
                  We developed a comprehensive WordPress and WooCommerce solution with custom online ordering, 
                  delivery integration, table reservation system, and customer loyalty program. The platform 
                  seamlessly integrates with their POS system and third-party delivery services.
                </p>
                <p>
                  The implementation resulted in a 250% increase in online orders, 40% overall revenue growth, 
                  and 65% improvement in customer retention through the loyalty program.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">6 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">December 2023</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team Size:</span>
                    <span className="font-medium">3 specialists</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete restaurant management solution with online ordering and customer engagement features.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Significant growth in online orders and customer engagement across all metrics.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <ShoppingCart className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Complex Menu Management</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Managing a complex menu with daily specials, seasonal items, and customization options 
                while maintaining accurate pricing and availability.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed a custom WooCommerce extension with advanced product 
                variations, real-time inventory tracking, and easy menu management interface.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Utensils className="h-8 w-8 text-orange-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Order Coordination</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Coordinating online orders with in-house dining and delivery services while maintaining 
                kitchen efficiency and order accuracy.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Integrated the system with their POS and kitchen display systems, 
                creating a unified order management workflow with real-time updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "OutstandingNet transformed our business during a critical time. The online ordering system 
            not only helped us survive but thrive, increasing our revenue by 40% in just six months."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Maria Rossi"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Maria Rossi</div>
              <div className="text-primary-200">Owner, Bella Vista Italian Bistro</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Grow Your Restaurant Business?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create an online ordering solution that drives revenue growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};